import {useMutation} from '@tanstack/react-query';
import React, {FC, useEffect, useMemo, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {usePrevious} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {DataEventObjectName} from '../../../../../libs/shared/core/src/models/tracking/dataEventObjectName';
import {OnboardingDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {postApiAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {createSelector} from '../../../../../srcCommon/helpers/reselectHelpers';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {ApiAdminOnboardingResources} from '../../../../models/api/onboardingModels';
import {SettingsRuleResourcePagesEnum} from '../../../../models/url/models/rulesUrlModel';
import {SettingsRootPagesEnum} from '../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../models/url/settingsUrlHelpers';
import {DelayParameter, delayToMilliseconds} from '../../../../settings/editors/commons/paramModel';
import {useInvalidateWorkflows} from '../../../../settings/fetchers/workflows';
import {getUserCircles} from '../../../../store/data/circles/circlesSelectors';
import {
  getActivationPreferences as getCompanyActivationPreferences,
  getCompany,
  getWorkflowsTaskIsComplete,
} from '../../../../store/data/company/companySelectors';
import {dataUpdatedAction} from '../../../../store/data/dataGroupsActions';
import {
  TeamBusinessHoursPreferencesUpdatedAction,
  TeamPreferencesActionsEnum,
} from '../../../../store/data/teamPreferences/teamPreferencesActions';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {getAdminSetupTeamInbox} from '../../../../store/stateless/onboardingSelectors';
import {useOnboardingModalState} from '../../../app/topBar/onboarding/onboardingContext';
import {OnboardingGuideAnimatedStyles, OnboardingGuidePopover} from '../../guide/onboardingGuidePopover';
import {
  OnboardingAdminWizardVisual,
  OnboardingAdminWizardVisualFrame,
} from '../../welcome/onboardingAdminWizardVisual';
import {OnboardingInboxVisualInboxPosition} from '../../welcome/onboardingInboxVisual';
import {
  OnboardingWorkflowsAssignRuleAssignAlgorithm,
  OnboardingWorkflowsAssignRuleSlide,
} from '../../welcome/workflows/onboardingWorkflowsAssignRuleSlide';
import {
  OnboardingWorkflowsCreatedSlide,
  RWorkflowGoal,
  WorkflowGoal,
} from '../../welcome/workflows/onboardingWorkflowsCreatedSlide';
import {OnboardingWorkflowsOutcomeSlide} from '../../welcome/workflows/onboardingWorkflowsOutcomeSlide';
import {OnboardingWorkflowsOutcomesV2} from '../../welcome/workflows/onboardingWorkflowsOutcomesV2';
import {OnboardingWorkflowsProgressSlide} from '../../welcome/workflows/onboardingWorkflowsProgressSlide';
import {OnboardingWorkflowsSlaRuleSlide} from '../../welcome/workflows/onboardingWorkflowsSlaRuleSlide';
import {
  OnboardingWorkflowsTagRuleCustomTagName,
  OnboardingWorkflowsTagRuleSlide,
  OnboardingWorkflowsTagRuleTagName,
  OnboardingWorkflowsTagRuleTagNamesConstants,
  parseCustomKeywords,
  parseCustomTagName,
} from '../../welcome/workflows/onboardingWorkflowsTagRuleSlide';
import {getProgressStages} from '../wizard/adminWizardPopover';

enum WorkflowsGuideSlidesEnum {
  WORKFLOWS = 'workflows',
  WORKFLOWS_CREATE_SLA_RULE = 'workflows_create_sla_rule',
  WORKFLOWS_CREATE_TAG_RULE = 'workflows_create_tag_rule',
  WORKFLOWS_CREATE_ASSIGN_RULE = 'workflows_create_assign_rule',
  PROGRESS = 'progress',
  CREATED_RESOURCES = 'created_resources',
}

const SLIDE_TRACKING_OBJECT_NAME: Record<WorkflowsGuideSlidesEnum, DataEventObjectName | null> = {
  [WorkflowsGuideSlidesEnum.WORKFLOWS]: OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS,
  [WorkflowsGuideSlidesEnum.WORKFLOWS_CREATE_SLA_RULE]:
    OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_SLA_RULE,
  [WorkflowsGuideSlidesEnum.WORKFLOWS_CREATE_TAG_RULE]:
    OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_TAG_RULE,
  [WorkflowsGuideSlidesEnum.WORKFLOWS_CREATE_ASSIGN_RULE]:
    OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_ASSIGN_RULE,
  [WorkflowsGuideSlidesEnum.PROGRESS]: null,
  [WorkflowsGuideSlidesEnum.CREATED_RESOURCES]: OnboardingDataEventObject.ADMIN_WIZARD_CREATED_RESOURCES,
};

const VISUAL_FRAME: Record<WorkflowsGuideSlidesEnum, OnboardingAdminWizardVisualFrame | null> = {
  [WorkflowsGuideSlidesEnum.WORKFLOWS]: OnboardingAdminWizardVisualFrame.GOALS,
  [WorkflowsGuideSlidesEnum.WORKFLOWS_CREATE_SLA_RULE]: OnboardingAdminWizardVisualFrame.SLA_RULE,
  [WorkflowsGuideSlidesEnum.WORKFLOWS_CREATE_TAG_RULE]: OnboardingAdminWizardVisualFrame.TAG_RULE,
  [WorkflowsGuideSlidesEnum.WORKFLOWS_CREATE_ASSIGN_RULE]: OnboardingAdminWizardVisualFrame.ASSIGN_RULE,
  [WorkflowsGuideSlidesEnum.PROGRESS]: null,
  [WorkflowsGuideSlidesEnum.CREATED_RESOURCES]: OnboardingAdminWizardVisualFrame.CREATED_RESOURCES,
};

const VISUAL_POSITION: Record<WorkflowsGuideSlidesEnum, OnboardingInboxVisualInboxPosition | null> = {
  [WorkflowsGuideSlidesEnum.WORKFLOWS]: OnboardingInboxVisualInboxPosition.CENTER,
  [WorkflowsGuideSlidesEnum.WORKFLOWS_CREATE_SLA_RULE]: OnboardingInboxVisualInboxPosition.RIGHT,
  [WorkflowsGuideSlidesEnum.WORKFLOWS_CREATE_TAG_RULE]: OnboardingInboxVisualInboxPosition.RIGHT,
  [WorkflowsGuideSlidesEnum.WORKFLOWS_CREATE_ASSIGN_RULE]: OnboardingInboxVisualInboxPosition.RIGHT,
  [WorkflowsGuideSlidesEnum.PROGRESS]: null,
  [WorkflowsGuideSlidesEnum.CREATED_RESOURCES]: OnboardingInboxVisualInboxPosition.CENTER,
};

/*
 * Props.
 */

export interface WorkflowsGuidePopoverProps {
  styles: OnboardingGuideAnimatedStyles | undefined;
  onClose: () => void;
  isWorkflowsGuideOpen: boolean;
  team: TeamModel;
}

/*
 * Component.
 */

export const WorkflowsGuidePopover: FC<WorkflowsGuidePopoverProps> = (props) => {
  const {onClose, styles, isWorkflowsGuideOpen, team} = props;

  const {trackDataEvent} = useDataEventTracking();
  const company = useAppSelector(getCompany);

  const isWorkflowsGuideCompleted = useAppSelector(getWorkflowsTaskIsComplete);

  const [slide, setSlide] = useState<WorkflowsGuideSlidesEnum>(
    computeInitialSlideState(isWorkflowsGuideCompleted ?? false),
  );

  const [selectedOutcomes, setSelectedOutcomes] = useState<ReadonlyArray<OnboardingWorkflowsOutcomesV2>>([]);
  const [slaReplyTimeGoal, setSlaReplyTimeGoal] = useState<DelayParameter | undefined>(undefined);
  const [tagName, setTagName] = useState<OnboardingWorkflowsTagRuleTagName | undefined>(undefined);
  const [customTagName, setCustomTagName] = useState('');
  const [suggestedKeywords, setSuggestedKeywords] = useState<ReadonlyArray<string>>([]);
  const [customKeywords, setCustomKeywords] = useState('');
  const [assignAlgorithm, setAssignAlgorithm] = useState<
    OnboardingWorkflowsAssignRuleAssignAlgorithm | undefined
  >(undefined);

  const {namespace} = team;

  const visualFrame = VISUAL_FRAME[slide];
  const visualPosition = VISUAL_POSITION[slide];
  const previousSlide = usePrevious(slide);
  const previousVisualPosition = previousSlide ? VISUAL_POSITION[previousSlide] : null;

  // If the user has already completed the guide, show them the resources they created.
  const shouldShowViewCreatedResources =
    (previousSlide === null || previousSlide === WorkflowsGuideSlidesEnum.CREATED_RESOURCES) &&
    slide === WorkflowsGuideSlidesEnum.CREATED_RESOURCES;

  const participatingInbox = useAppSelector(getAdminSetupTeamInbox);
  const visualInbox = participatingInbox ?? {color: 'grey', name: 'Shared inbox'};

  const inboxState = {
    name: participatingInbox?.name ?? 'Shared inbox',
    description: participatingInbox?.description ?? 'Shared inbox',
    color: participatingInbox?.color ?? 'grey',
    isPublic: participatingInbox?.isPublic ?? true,
    teammateIds: [],
    pinnedTeammateIds: [],
    circleIds: [],
    selectAllTeammatesWithAccess: true,
  };

  const dispatch = useAppDispatch();
  const invalidateWorkflows = useInvalidateWorkflows();

  const resourceMutation = useMutation({
    mutationFn: async (variables: {outcomes: ReadonlyArray<OnboardingWorkflowsOutcomesV2>}) => {
      const payload = {
        workflow_goals: generateWorkflowGoals(
          variables.outcomes,
          slaReplyTimeGoal,
          tagName,
          customTagName,
          suggestedKeywords,
          customKeywords,
          assignAlgorithm,
        ),
        inbox_id: participatingInbox?.id,
        circle_id: createdCircle?.id,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        has_onboarding_prefilled_workspace_v3: true,
      };

      const createdResources = await postApiAsync(
        ApiAdminOnboardingResources,
        `${company.url}/namespaces/${namespace}/admin-onboarding/resources`,
        payload,
      );

      dispatch(dataUpdatedAction({company: createdResources.company}));

      if (createdResources.businessHours) {
        const action: TeamBusinessHoursPreferencesUpdatedAction = {
          type: TeamPreferencesActionsEnum.TEAM_BUSINESS_HOURS_PREFERENCES_UPDATED,
          payload: {
            teamId: team.id,
            businessHours: {...createdResources.businessHours, hasUpdateError: false},
          },
        };
        dispatch(action);
      }

      invalidateWorkflows();
      return createdResources;
    },
  });

  useEffect(() => {
    const eventName = SLIDE_TRACKING_OBJECT_NAME[slide];
    if (!isWorkflowsGuideOpen || !eventName) {
      return;
    }

    trackDataEvent(DataEventActionsEnum.VIEW, eventName);
  }, [isWorkflowsGuideOpen, trackDataEvent, slide]);

  const onCancel = () => {
    trackDataEvent(
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_ASSIGN_RULE_SKIP,
    );
    setSlide(WorkflowsGuideSlidesEnum.WORKFLOWS);
    onClose();
  };

  const companyActivationPreferences = useAppSelector(getCompanyActivationPreferences);

  const {openAdminSetupGuideSetupScreen} = useOnboardingModalState();

  const getCreatedCircle = useMemo(
    () => makeGetCircleById(companyActivationPreferences.adminOnboardingResources.circleId),
    [companyActivationPreferences.adminOnboardingResources.circleId],
  );
  const createdCircle = useAppSelector(getCreatedCircle);

  const onDone = async () => {
    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.ADMIN_WIZARD_DONE);

    onClose();

    openAdminSetupGuideSetupScreen();
  };

  const onNavigateToRules = (rulesNamespace: string) => {
    trackDataEvent(
      DataEventActionsEnum.CLICK,
      OnboardingDataEventObject.ADMIN_WIZARD_CREATED_RESOURCES_NAVIGATE_TO_SETTINGS_RULES,
    );
    dispatch(
      navigateToLinkAction(
        buildSettingsPath({
          namespace: rulesNamespace,
          rootPage: SettingsRootPagesEnum.RULES,
          resourcePage: SettingsRuleResourcePagesEnum.OVERVIEW,
          isInboxCreation: false,
          inboxId: undefined,
          ruleId: undefined,
          recipe: undefined,
          bundleId: undefined,
          filters: undefined,
        }),
      ),
    );
    onClose();
  };

  const navigateToPreviousWorkflowsSlide = () => setSlide(getPreviousWorkflowsSlide(slide, selectedOutcomes));

  const navigateToNextWorkflowsSlide = () => {
    const {nextSlide, isLastSlide} = getNextWorkflowsSlide(slide, selectedOutcomes);

    setSlide(nextSlide);

    if (isLastSlide) {
      resourceMutation.mutate({outcomes: selectedOutcomes});
    }
  };

  const renderSlide = () => {
    switch (slide) {
      case WorkflowsGuideSlidesEnum.WORKFLOWS:
        return (
          <OnboardingWorkflowsOutcomeSlide
            selectedOutcomes={selectedOutcomes}
            onChangeOutcomes={setSelectedOutcomes}
            navPrimary={
              <ButtonGroup>
                <Button
                  variant={ButtonVariantsEnum.SECONDARY}
                  onClick={() => {
                    setSelectedOutcomes([]);
                    onClose();
                    trackDataEvent(
                      DataEventActionsEnum.CLICK,
                      OnboardingDataEventObject.ADMIN_WIZARD_INBOX_GOALS_SKIP,
                    );
                  }}
                >
                  <Trans id="onboardingwelcome-workflows-slide-skip">Cancel</Trans>
                </Button>
                <Button
                  variant={ButtonVariantsEnum.PRIMARY}
                  isDisabled={selectedOutcomes.length === 0}
                  onClick={() => {
                    trackDataEvent(
                      DataEventActionsEnum.CLICK,
                      OnboardingDataEventObject.ADMIN_WIZARD_INBOX_GOALS_NEXT,
                      {inbox_goals: selectedOutcomes},
                    );
                    navigateToNextWorkflowsSlide();
                  }}
                >
                  <Trans id="onboardingwelcome-workflows-slide-next-v2">Next</Trans>
                </Button>
              </ButtonGroup>
            }
          />
        );
      case WorkflowsGuideSlidesEnum.WORKFLOWS_CREATE_SLA_RULE:
        return (
          <OnboardingWorkflowsSlaRuleSlide
            slaReplyTimeGoal={slaReplyTimeGoal}
            onChangeSlaReplyTimeGoal={setSlaReplyTimeGoal}
            navPrimary={renderWorkflowsCreateRuleNavigation({
              onClickSkip: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_SLA_RULE_SKIP,
                );
                setSlaReplyTimeGoal(undefined);
                navigateToNextWorkflowsSlide();
              },
              onNavigateToPreviousWorkflowsSlide: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_SLA_RULE_PREVIOUS,
                );
                navigateToPreviousWorkflowsSlide();
              },
              onNavigateToNextWorkflowsSlide: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_SLA_RULE_NEXT,
                );
                navigateToNextWorkflowsSlide();
              },
              isNextDisabled: !(slaReplyTimeGoal && delayToMilliseconds(slaReplyTimeGoal) > 0),
            })}
          />
        );
      case WorkflowsGuideSlidesEnum.WORKFLOWS_CREATE_TAG_RULE:
        return (
          <OnboardingWorkflowsTagRuleSlide
            tagName={tagName}
            onChangeTagName={setTagName}
            customTagName={customTagName}
            onChangeCustomTagName={setCustomTagName}
            suggestedKeywords={suggestedKeywords}
            onChangeSuggestedKeywords={setSuggestedKeywords}
            customKeywords={customKeywords}
            onChangeCustomKeywords={setCustomKeywords}
            navPrimary={renderWorkflowsCreateRuleNavigation({
              onClickSkip: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_TAG_RULE_SKIP,
                );
                setTagName(undefined);
                setCustomTagName('');
                setSuggestedKeywords([]);
                setCustomKeywords('');
                navigateToNextWorkflowsSlide();
              },
              onNavigateToPreviousWorkflowsSlide: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_TAG_RULE_PREVIOUS,
                );
                navigateToPreviousWorkflowsSlide();
              },
              onNavigateToNextWorkflowsSlide: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_TAG_RULE_NEXT,
                );
                navigateToNextWorkflowsSlide();
              },
              isNextDisabled: !tagGoalIsComplete(tagName, customTagName, suggestedKeywords, customKeywords),
            })}
          />
        );
      case WorkflowsGuideSlidesEnum.WORKFLOWS_CREATE_ASSIGN_RULE:
        return (
          <OnboardingWorkflowsAssignRuleSlide
            inboxState={inboxState}
            createdCircle={createdCircle}
            assignAlgorithm={assignAlgorithm}
            onChangeAssignAlgorithm={setAssignAlgorithm}
            navPrimary={renderWorkflowsCreateRuleNavigation({
              onClickSkip: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_ASSIGN_RULE_SKIP,
                );
                setAssignAlgorithm(undefined);
                navigateToNextWorkflowsSlide();
              },
              onNavigateToPreviousWorkflowsSlide: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_ASSIGN_RULE_PREVIOUS,
                );
                navigateToPreviousWorkflowsSlide();
              },
              onNavigateToNextWorkflowsSlide: () => {
                trackDataEvent(
                  DataEventActionsEnum.CLICK,
                  OnboardingDataEventObject.ADMIN_WIZARD_WORKFLOWS_CREATE_ASSIGN_RULE_NEXT,
                );
                navigateToNextWorkflowsSlide();
              },
              isNextDisabled: assignAlgorithm === undefined,
            })}
          />
        );
      case WorkflowsGuideSlidesEnum.PROGRESS:
        return (
          <OnboardingWorkflowsProgressSlide
            stages={getProgressStages(
              Object.keys(
                generateWorkflowGoals(
                  selectedOutcomes,
                  slaReplyTimeGoal,
                  tagName,
                  customTagName,
                  suggestedKeywords,
                  customKeywords,
                  assignAlgorithm,
                ),
              ).filter(RWorkflowGoal.is),
            )}
            isLoading={resourceMutation.isLoading}
            onFinished={() => setSlide(WorkflowsGuideSlidesEnum.CREATED_RESOURCES)}
          />
        );

      case WorkflowsGuideSlidesEnum.CREATED_RESOURCES:
        return (
          <OnboardingWorkflowsCreatedSlide
            navPrimary={
              <Button variant={ButtonVariantsEnum.PRIMARY} onClick={() => onDone()}>
                <Trans id="onboardingwelcome-workflows-created-slide-done">Done</Trans>
              </Button>
            }
            adminOnboardingResources={{
              ...companyActivationPreferences.adminOnboardingResources,
              inboxId: null,
              circleId: null,
            }}
            shouldShowViewCreatedResources={shouldShowViewCreatedResources}
            companyName={company.name}
            team={team}
            onNavigateToRules={onNavigateToRules}
            inbox={visualInbox}
            tags={resourceMutation.data?.tags ?? []}
            rules={resourceMutation.data?.rules ?? []}
          />
        );
      default:
        throw new UnreachableError(slide);
    }
  };

  return (
    <OnboardingGuidePopover styles={styles} onRequestClose={onCancel}>
      {renderSlide()}

      {visualFrame !== null && (
        <OnboardingAdminWizardVisual
          frame={visualFrame}
          selectedTeamType=""
          teamDisplayName=""
          initialHasTeamType={false}
          inboxState={inboxState}
          companyName={company.name}
          createdCircle={undefined}
          position={visualPosition ?? undefined}
          previousPosition={previousVisualPosition ?? undefined}
          team={team}
          slaRuleSlaReplyTimeGoal={slaReplyTimeGoal}
          tagRuleTagName={
            tagName === OnboardingWorkflowsTagRuleCustomTagName.ENTER_SOMETHING_ELSE
              ? parseCustomTagName(customTagName)
              : (tagName && OnboardingWorkflowsTagRuleTagNamesConstants[tagName].displayName) ?? ''
          }
          tagRuleKeywords={[...new Set([...suggestedKeywords, ...parseCustomKeywords(customKeywords)])]}
        />
      )}
    </OnboardingGuidePopover>
  );
};

const makeGetCircleById = (circleId: number | null) =>
  createSelector([getUserCircles], (circles) => circles.find((circle) => circle.id === circleId));

function computeInitialSlideState(isWorkflowsGuideCompleted: boolean): WorkflowsGuideSlidesEnum {
  if (isWorkflowsGuideCompleted) {
    return WorkflowsGuideSlidesEnum.CREATED_RESOURCES;
  }

  return WorkflowsGuideSlidesEnum.WORKFLOWS;
}

function generateWorkflowGoals(
  workflowOutcomes: ReadonlyArray<OnboardingWorkflowsOutcomesV2>,
  slaReplyTime: DelayParameter | undefined,
  tagName: OnboardingWorkflowsTagRuleTagName | undefined,
  customTagName: string,
  suggestedKeywords: ReadonlyArray<string>,
  customKeywords: string,
  assignAlgorithm: OnboardingWorkflowsAssignRuleAssignAlgorithm | undefined,
) {
  const slaGoal =
    workflowOutcomes.includes(OnboardingWorkflowsOutcomesV2.NO_CUSTOMER_WAITING) &&
    slaReplyTime &&
    delayToMilliseconds(slaReplyTime) > 0
      ? {
          [WorkflowGoal.ENSURE_TIMELY_RESPONSE_TO_CUSTOMERS]: {
            sla_reply_time_goal_ms: delayToMilliseconds(slaReplyTime),
          },
        }
      : {};

  const tagGoal =
    workflowOutcomes.includes(OnboardingWorkflowsOutcomesV2.HANDLED) &&
    tagName !== undefined &&
    tagGoalIsComplete(tagName, customTagName, suggestedKeywords, customKeywords)
      ? {
          [WorkflowGoal.UNDERSTAND_CONVERSATION_TRENDS]: {
            tag_name:
              tagName === OnboardingWorkflowsTagRuleCustomTagName.ENTER_SOMETHING_ELSE
                ? parseCustomTagName(customTagName)
                : OnboardingWorkflowsTagRuleTagNamesConstants[tagName].displayName,
            keywords: [...new Set([...suggestedKeywords, ...parseCustomKeywords(customKeywords)])],
          },
        }
      : {};

  const assignGoal =
    workflowOutcomes.includes(OnboardingWorkflowsOutcomesV2.CATEGORIZED) && assignAlgorithm
      ? {
          [WorkflowGoal.GIVE_EACH_CONVERSATION_A_CLEAR_OWNER]: {
            assign_algorithm: assignAlgorithm,
          },
        }
      : {};

  return {
    ...slaGoal,
    ...tagGoal,
    ...assignGoal,
  };
}

function mapWorkflowsOutcomeToSlide(
  workflowsOutcome: OnboardingWorkflowsOutcomesV2,
): WorkflowsGuideSlidesEnum {
  switch (workflowsOutcome) {
    case OnboardingWorkflowsOutcomesV2.NO_CUSTOMER_WAITING:
      return WorkflowsGuideSlidesEnum.WORKFLOWS_CREATE_SLA_RULE;
    case OnboardingWorkflowsOutcomesV2.HANDLED:
      return WorkflowsGuideSlidesEnum.WORKFLOWS_CREATE_TAG_RULE;
    case OnboardingWorkflowsOutcomesV2.CATEGORIZED:
      return WorkflowsGuideSlidesEnum.WORKFLOWS_CREATE_ASSIGN_RULE;
    default:
      throw new UnreachableError(workflowsOutcome);
  }
}

function getWorkflowsSlides(workflowsOutcomes: ReadonlyArray<OnboardingWorkflowsOutcomesV2>) {
  const workflowsOutcomesOrder = [
    OnboardingWorkflowsOutcomesV2.NO_CUSTOMER_WAITING,
    OnboardingWorkflowsOutcomesV2.HANDLED,
    OnboardingWorkflowsOutcomesV2.CATEGORIZED,
  ];

  const sortedWorkflowsOutcomes = [...workflowsOutcomes];
  sortedWorkflowsOutcomes.sort(
    (a, b) => workflowsOutcomesOrder.indexOf(a) - workflowsOutcomesOrder.indexOf(b),
  );

  return sortedWorkflowsOutcomes.map((outcome) => mapWorkflowsOutcomeToSlide(outcome));
}

function getPreviousWorkflowsSlide(
  slide: WorkflowsGuideSlidesEnum,
  workflowsOutcomes: ReadonlyArray<OnboardingWorkflowsOutcomesV2>,
) {
  const workflowSlides = getWorkflowsSlides(workflowsOutcomes);
  const slideIndex = workflowSlides.indexOf(slide);
  const nextSlide = slideIndex === -1 ? workflowSlides[0] : workflowSlides[slideIndex - 1];

  if (!nextSlide) {
    return WorkflowsGuideSlidesEnum.WORKFLOWS;
  }

  return nextSlide;
}

function getNextWorkflowsSlide(
  slide: WorkflowsGuideSlidesEnum,
  workflowsOutcomes: ReadonlyArray<OnboardingWorkflowsOutcomesV2>,
) {
  const workflowSlides = getWorkflowsSlides(workflowsOutcomes);
  const slideIndex = workflowSlides.indexOf(slide);
  const nextSlide = slideIndex === -1 ? workflowSlides[0] : workflowSlides[slideIndex + 1];

  if (!nextSlide) {
    return {nextSlide: WorkflowsGuideSlidesEnum.PROGRESS, isLastSlide: true};
  }

  return {nextSlide, isLastSlide: false};
}

function tagGoalIsComplete(
  tagRuleTagName: OnboardingWorkflowsTagRuleTagName | undefined,
  tagRuleCustomTagName: string,
  tagRuleSuggestedKeywords: ReadonlyArray<string>,
  tagRuleCustomKeywords: string,
) {
  return Boolean(
    (tagRuleTagName === OnboardingWorkflowsTagRuleCustomTagName.ENTER_SOMETHING_ELSE
      ? parseCustomTagName(tagRuleCustomTagName)
      : tagRuleTagName) &&
      (tagRuleSuggestedKeywords.length || parseCustomKeywords(tagRuleCustomKeywords).length),
  );
}

function renderWorkflowsCreateRuleNavigation(props: {
  onClickSkip: () => void;
  onNavigateToPreviousWorkflowsSlide: () => void;
  onNavigateToNextWorkflowsSlide: () => void;
  isNextDisabled: boolean;
}) {
  const {onClickSkip, onNavigateToPreviousWorkflowsSlide, onNavigateToNextWorkflowsSlide, isNextDisabled} =
    props;

  return (
    <ButtonGroup>
      <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onClickSkip}>
        <Trans id="onboardinginbox-skip">Skip this step</Trans>
      </Button>
      <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onNavigateToPreviousWorkflowsSlide}>
        <Trans id="onboardinginbox-previous">Previous</Trans>
      </Button>
      <Button
        variant={ButtonVariantsEnum.PRIMARY}
        isDisabled={isNextDisabled}
        onClick={onNavigateToNextWorkflowsSlide}
      >
        <Trans id="onboardinginbox-next">Next</Trans>
      </Button>
    </ButtonGroup>
  );
}
